import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, type ReactNode } from 'react';
import { api, ErroApi } from './api.ts';

export interface Permissoes {
  admin: boolean;
  pode_exame_consultar: boolean;
  pode_exame_cadastrar: boolean;
  pode_exame_alterar: boolean;
  pode_excluir_imagens: boolean;
  pode_convenios: boolean;
  pode_pacientes: boolean;
  pode_empresas: boolean;
  pode_ocupacional: boolean;
  pode_avisar_medicos: boolean;
  pode_avisar_pacientes: boolean;
  pode_procedimentos: boolean;
  pode_medicos: boolean;
  pode_relatorios: boolean;
  pode_usuarios: boolean;
  pode_financeiro: boolean;
}

export type Papel = 'usuario' | 'medico';

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  admin: boolean;
  trocarSenha: boolean;
  papel: Papel;
  permissoes: Permissoes;
  /** Médico */
  crm?: string;
  temAssinatura?: boolean;
  podeLaudar?: boolean;
  /** Empresa */
  cnpj?: string;
}

const Contexto = createContext<{ usuario: Usuario | null; carregando: boolean } | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const { data, isPending } = useQuery({
    queryKey: ['sessao'],
    queryFn: async () => {
      try {
        return await api.get<Usuario>('/auth/eu');
      } catch (e) {
        if (e instanceof ErroApi && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  return <Contexto value={{ usuario: data ?? null, carregando: isPending }}>{children}</Contexto>;
}

export function useSessao() {
  const ctx = use(Contexto);
  if (!ctx) throw new Error('useSessao precisa estar dentro de ProvedorSessao.');
  return ctx;
}

/** Usuario logado, garantido. So use dentro de rotas protegidas. */
export function useUsuario(): Usuario {
  const { usuario } = useSessao();
  if (!usuario) throw new Error('Sem sessao.');
  return usuario;
}

export function usePode() {
  const { usuario } = useSessao();
  return (...quais: (keyof Permissoes)[]) => {
    if (!usuario) return false;
    if (usuario.permissoes?.admin) return true;
    return quais.some((q) => usuario.permissoes?.[q]);
  };
}

/** Para onde cada papel vai ao entrar.
 *
 *  O médico que não lauda cai em outra tela: mandá-lo para a fila de laudo o
 *  receberia com uma lista sempre vazia. */
export function inicioDoPapel(
  papel: Papel | undefined,
  podeLaudar = true,
  admin = false,
): string {
  if (papel === 'medico') return podeLaudar ? '/laudar' : '/meus-pedidos';
  /* Só o administrador começa no painel. A recepção cai direto na fila, que é
     onde ela trabalha o dia inteiro: um resumo antes do trabalho seria uma tela
     a mais entre ela e o balcão. */
  return admin ? '/inicio' : '/fila';
}

export function useSair() {
  const qc = useQueryClient();
  return async () => {
    await api.post('/auth/sair');
    qc.clear();
    window.location.href = '/entrar';
  };
}
